import { OrderLegQuery, OrderLegResult } from '@thinkalpha/platform-ws-client/contracts/trading.js';
import type {
    OrderLegQuery as OrderLegQueryType,
    OrderLegResult as OrderLegResultType,
} from '@thinkalpha/platform-ws-client/contracts/trading.js';
import { api } from 'src/api';
import { container } from 'src/StaticContainer';

const logger = container.get('Logger');

/**
 * Error thrown when legs are not found for an order (404 response).
 * This allows the query client retry logic to handle retries with backoff.
 */
export class LegsNotFoundError extends Error {
    constructor(readonly clOrdId: string) {
        super(`Legs not found for order ${clOrdId}`);
        this.name = 'LegsNotFoundError';
    }
}

// Response is an array of OrderLegResult
const OrderLegResultArray = OrderLegResult.array();

/**
 * Fetches the legs for a multi-leg order by clOrdId.
 * POST /trading/leg-queries (platform-ws API)
 *
 * @param clOrdId - The client order ID to fetch legs for
 * @returns Array of OrderLegResult objects, or empty array if not found
 */
export const fetchLegsForOrder = (clOrdId: string): Promise<OrderLegResultType[]> => {
    // Validate input
    const query: OrderLegQueryType = OrderLegQuery.parse({ clOrdId });

    return api
        .post('/trading/leg-queries', query)
        .then((response) => OrderLegResultArray.parse(response.data))
        .catch((e) => {
            // 404 means legs not yet available - throw to trigger retry with backoff
            if (e.response?.status === 404) {
                throw new LegsNotFoundError(clOrdId);
            }
            logger.error('Failed to fetch legs for order.', { error: e, clOrdId });
            throw e;
        });
};
